import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  ErrorModal,
  Inscription,
  onlyDateToServer,
  Preloader,
  useResponsive,
} from '@common-fe/common-fe';

import { MobilePageModalWrapper, ModalWrapper } from '@/components';
import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';
import DependentForm from '@/modules/transaction/components/ReimburseMe/ClaimInfo/AddPatienForm/PatientForm';
import { Patient } from '@/modules/transaction/components/ReimburseMe/ClaimInfo/usePatients.query';
import useUpdateDependentQuery from '@/modules/user/components/ManageProfile/components/Dependents/queries/useUpdateDependent.query';
import { useStore } from '@/modules/user/components/ManageProfile/components/Dependents/stores/useDependents.store';
import useHighlightedDependentStore from '@/modules/user/components/ManageProfile/components/Dependents/stores/useHighlightedDependent.store';
import useHighlightById from '@/utils/hooks/useHighlightById';
import useSsnConflictError from '@/utils/hooks/useSsnConflictError';

interface Props {
  onSetVisible: (value: boolean) => void;
}

const EditDependentModal = ({ onSetVisible }: Props) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const [isError, setIsError] = useState(false);
  const [isSsnConflictVisible, setSsnConflictVisible] = useState(false);
  const handleSsnConflictError = useSsnConflictError({ setSsnConflictVisible });
  const { handleAddPermanentSnackbar } = useSnackbar();
  const { editableDependent, handleReset } = useStore();
  const editableDependentId = editableDependent?.id;
  const { updateDependent, isLoading } = useUpdateDependentQuery(editableDependentId);
  const { reset, setHighlightedDependentId } = useHighlightedDependentStore();
  const { setHighlightedById } = useHighlightById({ onSetHighlightedId: setHighlightedDependentId, reset });

  const handleClose = useCallback(() => {
    onSetVisible(false);
  }, [onSetVisible]);

  const onSuccess = useCallback(() => {
    setHighlightedById(editableDependentId);
    handleAddPermanentSnackbar({
      text: `${editableDependent?.firstName} ${editableDependent?.lastName} was updated successfully!`,
      state: SnackbarState.positive,
      closeIcon: true,
      autoRemove: true,
    });
    setTimeout(() => handleClose());
  }, [handleClose, handleAddPermanentSnackbar, setHighlightedById, editableDependentId, editableDependent]);

  const handleSubmit = useCallback(
    async (value: Patient) => {
      try {
        setIsError(false);
        await updateDependent({
          firstName: value.firstName,
          lastName: value.lastName,
          ssn: value.ssn,
          birthOn: onlyDateToServer(value.birthOn),
          relationshipType: value.relationshipType,
        });
        onSuccess();
      } catch (e) {
        setIsError(!handleSsnConflictError(e));
      }
    },
    [updateDependent, onSuccess, handleSsnConflictError]
  );

  useEffect(() => {
    return () => {
      handleReset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const form = (
    <DependentForm
      onSubmit={handleSubmit}
      defaultValue={editableDependent as Partial<Patient>}
      footer={
        <Box fill={isMobile} pad={{ top: 'spacing24' }} margin={isMobile ? { top: 'auto' } : undefined}>
          {isError && (
            <Inscription alignSelf="end" color="danger" margin={{ bottom: 'spacing24' }}>
              {t('Something goes wrong!')}
            </Inscription>
          )}
          {isMobile ? (
            <Box
              fill
              direction="row"
              justify="end"
              gap="spacing12"
              align="end"
              pad={{ horizontal: 'spacing8', bottom: 'spacing8' }}
            >
              <div style={{ flex: 1 }}>
                <AppButton
                  type="button"
                  onClick={handleClose}
                  buttonType="secondary"
                  width="100%"
                  testId="close"
                >
                  {t('Cancel')}
                </AppButton>
              </div>
              <div style={{ flex: 1 }}>
                <AppButton type="submit" width="100%" testId="submit">
                  {isLoading ? <Preloader color="white" /> : t('Save')}
                </AppButton>
              </div>
            </Box>
          ) : (
            <Box direction="row" justify="end" align="center" gap="spacing12">
              <AppButton
                type="button"
                onClick={handleClose}
                buttonType="secondary"
                width="control"
                testId="close"
              >
                {t('Cancel')}
              </AppButton>
              <AppButton type="submit" width="control" testId="submit">
                {isLoading ? <Preloader color="white" /> : t('Save')}
              </AppButton>
            </Box>
          )}
        </Box>
      }
    />
  );

  if (isMobile) {
    return (
      <>
        <MobilePageModalWrapper title={t('Edit a Dependent')} isVisible onSetVisible={() => handleClose()}>
          <Box pad={{ horizontal: 'spacing2' }} style={{ flex: 1 }}>
            {form}
          </Box>
        </MobilePageModalWrapper>
        <ErrorModal
          header={t('SSN must be unique')}
          buttonText={t('Got It!')}
          visible={isSsnConflictVisible}
          onSetVisible={setSsnConflictVisible}
        />
      </>
    );
  }

  return (
    <>
      <ModalWrapper
        visible
        title={t('Edit a Dependent')}
        onSetVisible={onSetVisible}
        testId="EditDependent"
        isClosingByClickOutsideBlocked={isSsnConflictVisible}
      >
        {form}
      </ModalWrapper>
      <ErrorModal
        header={t('SSN must be unique')}
        buttonText={t('Got It!')}
        visible={isSsnConflictVisible}
        onSetVisible={setSsnConflictVisible}
      />
    </>
  );
};

export default EditDependentModal;
